/**
 * Authenticated HTTP client with automatic JWT handling
 *
 * Extends the base HTTP client (httpBaseClient) with JWT authentication capabilities.
 * Inherits all base client features including retry logic, timeout handling, and
 * Netlify timeout detection/transformation.
 *
 * This is the main HTTP client for the billing application. It automatically:
 * - Initializes JWT tokens from Ghost Admin on first request
 * - Injects JWT into all request bodies for backend authentication
 * - Refreshes expired tokens and retries failed requests
 * - Maintains session with automatic renewal every hour
 *
 * Usage:
 * ```javascript
 * import httpClient from '../http/httpClient';
 *
 *
 * // POST request with data
 * const result = await httpClient.post('/api/update-card', {
 *   cardToken: 'tok_xxx'
 * });
 * ```
 *
 * Authentication Flow:
 * 1. Request interceptor initializes JWTokenHandler on first request to obtain JWT from Ghost Admin
 * 2. JWTokenHandler exchanges JWT for bearer token at /api/billing-session endpoint
 * 3. Request interceptor injects JWT from JWTokenHandler into all outgoing requests
 * 4. JWTokenHandler maintains the billing session with automatic renewal every hour (90s in dev)
 * 5. Response interceptor handles 401/403 errors by refreshing tokens via JWTokenHandler and retrying
 *
 * Request Format:
 * All requests are automatically wrapped in the backend's expected format:
 * ```javascript
 * {
 *   jwt: "eyJ...",        // JWT token from Ghost Admin
 *   data: {...},          // Your actual request data
 *   options: {...}        // Optional parameters
 * }
 * ```
 *
 * Error Handling:
 * - Network errors: Inherited retry logic from base client (3 attempts with exponential backoff)
 * - Netlify timeouts: Inherited from base client - detected and transformed to user-friendly errors
 * - Auth errors (401/403): Automatic token refresh and single retry
 * - Expired tokens: Detected via regex pattern, triggers refresh
 *
 * Important Notes:
 * - This client is a singleton - only one instance maintains the session
 * - Call cleanup() when unmounting to clear session intervals
 * - JWT is stored in memory by JWTokenHandler
 * - Bearer token is stored in encrypted `ghost-billing-token` cookie set by /api/billing-session
 * - Cookie handling is automatic via browser
 */

import { createHttpClient as createHttpBaseClient } from './httpBaseClient';
import JWTokenHandler from '../utils/JWTokenHandler';
import { injectJWT, handleAuthErrors } from './middleware';

// Create the singleton dependencies
const tokenHandler = new JWTokenHandler({
    verbose: false,
});

const baseHttpClient = createHttpBaseClient();

// Track JWT token handler initialization state
let isJWTHandlerInitialized = false;

// Configure axios interceptors
baseHttpClient.interceptors.request.use(
    injectJWT({
        tokenHandler,
        isTokenHandlerInitialized: () => isJWTHandlerInitialized,
        setTokenHandlerInitialized: (value) => {
            isJWTHandlerInitialized = value;
        },
    }),
    (error) => Promise.reject(error),
);

baseHttpClient.interceptors.response.use(
    (response) => response,
    handleAuthErrors({ tokenHandler, httpClient: baseHttpClient }),
);

// Add cleanup method to the axios instance
baseHttpClient.cleanup = () => {
    tokenHandler.clearSessionInterval();
    isJWTHandlerInitialized = false;
};

// Export the authenticated axios instance
export const httpClient = baseHttpClient;

// Export as default for convenience
export default httpClient;
